// Servizio per la gestione dell'autenticazione con API HealthNET

import { User } from '../types';
import { API_BASE_URL } from '../config';

// Definizione dei tipi per le credenziali di login
interface LoginCredentials {
    username: string;
    password: string;
}

// Definizione del tipo per la risposta di login
interface LoginResponse {
    token: string;
    user: User;
}

// Definizione del tipo per la risposta OAuth
interface OAuthTokenResponse {
    access_token: string;
    token_type: string;
    expires_in: number;
    scope: string;
}


// Servizio di autenticazione con API HealthNET
export const authService = {
    /**
     * Autentica l'utente utilizzando l'API backend locale
     * Utilizza l'endpoint /api/auth/login
     */
    login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
        try {
            console.log('Attempting login with backend API');
            
            // Step 1: Chiamare il backend locale per l'autenticazione
            const tokenResponse = await fetch(`${API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: credentials.username,
                    password: credentials.password
                })
            });

            if (!tokenResponse.ok) {
                const errorData = await tokenResponse.json();
                console.error('Token request failed:', errorData);
                throw new Error(errorData.message || 'Autenticazione fallita');
            }

            const tokenData = await tokenResponse.json();
            console.log('Token obtained successfully:', tokenData);

            // Step 2: Verificare che il token sia valido
            if (!tokenData.token || tokenData.status !== 'success') {
                throw new Error('Token non valido ricevuto dall\'API');
            }

            // Step 3: Salvare il token nel localStorage
            localStorage.setItem('authToken', tokenData.token);

            // Step 4: Utilizzare i dati utente dalla risposta
            const user: User = tokenData.data?.user || {
                id: tokenData.data?.user?.id || 'user_1',
                username: tokenData.data?.user?.username || credentials.username,
                email: tokenData.data?.user?.email || `${credentials.username}@local`,
                role: tokenData.data?.user?.role || 'user',
                createdAt: tokenData.data?.user?.createdAt || new Date(),
                updatedAt: tokenData.data?.user?.updatedAt || new Date()
            };

            return {
                token: tokenData.token,
                user: user
            };

        } catch (error: any) {
            console.error('HealthNET API login error:', error);
            
            // Gestione degli errori specifici
            if (error.message.includes('fetch')) {
                throw new Error('Impossibile connettersi all\'API HealthNET. Verificare la connessione.');
            } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
                throw new Error('Credenziali client non valide. Verificare configurazione.');
            } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
                throw new Error('Accesso negato. Contattare l\'amministratore.');
            } else if (error.message.includes('500')) {
                throw new Error('Errore del server HealthNET. Riprovare più tardi.');
            } else {
                throw new Error(error.message || 'Errore durante l\'autenticazione');
            }
        }
    },

    /**
     * Verifica la validità del token corrente
     * Per ora restituisce sempre true, da implementare se l'API supporta la verifica
     */
    verifyToken: async (token: string): Promise<boolean> => {
        try {
            // Per ora assumiamo che se il token esiste sia valido
            // L'API HealthNET potrebbe non avere un endpoint di verifica
            return !!token;
        } catch (error) {
            console.error('Token verification error:', error);
            return false;
        }
    },

    /**
     * Ottiene informazioni utente dal token
     * Per ora restituisce dati mock, da implementare se l'API supporta questo endpoint
     */
    getUserFromToken: async (token: string): Promise<User | null> => {
        try {
            // Per ora restituiamo dati mock
            // L'API HealthNET potrebbe non avere un endpoint per ottenere info utente dal token
            return {
                id: 'healthnet_user',
                username: 'user',
                email: 'user@healthnet.local',
                role: 'user',
                createdAt: new Date(),
                updatedAt: new Date()
            };
        } catch (error) {
            console.error('Get user from token error:', error);
            return null;
        }
    },

    /**
     * Effettua il logout
     */
    logout: async (token?: string) => {
        try {
            // Per ora rimuoviamo solo il token locale
            // L'API HealthNET potrebbe non avere un endpoint di logout
            localStorage.removeItem('authToken');
            console.log('Logout completed');
            
        } catch (error) {
            console.error('Logout error:', error);
            // Rimuovi comunque il token locale
            localStorage.removeItem('authToken');
        }
    },

    /**
     * Refresh del token
     * Per ora non implementato, l'API HealthNET potrebbe non supportarlo
     */
    refreshToken: async (currentToken: string): Promise<string | null> => {
        try {
            // Per ora non implementato
            console.log('Token refresh not implemented for HealthNET API');
            return null;
        } catch (error) {
            console.error('Token refresh error:', error);
            return null;
        }
    },

    /**
     * Ottiene il token corrente dal localStorage
     */
    getToken: (): string | null => {
        return localStorage.getItem('authToken');
    }
};
